import { useEffect, useState } from 'react';
import { FaCheck, FaSpinner } from 'react-icons/fa';

export interface ProgressStage {
  key: string;
  label: string;
  skip?: boolean;
}

interface OnboardingProgressProps {
  stages: ProgressStage[];
  isSubmitting: boolean;
  isComplete: boolean;
  error?: string | null;
}

/**
 * Displays a simulated stage-based progress indicator during the onboarding submit.
 * Stages advance on fixed timing while the request is in flight. When the request
 * completes, all remaining stages jump to done. If an error occurs, the current
 * stage is marked failed.
 *
 * This is simulated — not tied to real backend progress — but the stage order matches
 * the backend operation order so timing feels honest.
 */
export function OnboardingProgress({ stages, isSubmitting, isComplete, error }: OnboardingProgressProps) {
  const activeStages = stages.filter((s) => !s.skip);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!isSubmitting) return;

    setActiveIndex(0);

    // Advance through stages with a realistic pace. Total budget ~6s which is comfortably
    // longer than typical backend time for small portfolios. If the real request finishes
    // first, the completion effect below fast-forwards us.
    const perStageMs = 1000;
    const timers: ReturnType<typeof setTimeout>[] = [];

    activeStages.forEach((_, idx) => {
      if (idx === 0) return;
      const t = setTimeout(() => {
        setActiveIndex((prev) => Math.max(prev, idx));
      }, idx * perStageMs);
      timers.push(t);
    });

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [isSubmitting, activeStages.length]);

  // Jump to done when backend responds
  useEffect(() => {
    if (isComplete) {
      setActiveIndex(activeStages.length);
    }
  }, [isComplete, activeStages.length]);

  const percent = Math.min(
    100,
    Math.round(((isComplete ? activeStages.length : activeIndex) / Math.max(activeStages.length, 1)) * 100),
  );

  return (
    <div className="p-6 rounded-lg border border-border bg-surface-base">
      <ul className="space-y-3 mb-6">
        {activeStages.map((stage, idx) => {
          const isDone = idx < activeIndex || isComplete;
          const isActive = idx === activeIndex && !isComplete;
          const isPending = idx > activeIndex && !isComplete;
          const isFailed = !!error && isActive;

          return (
            <li key={stage.key} className="flex items-center gap-3 text-sm">
              <span
                className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isDone
                    ? 'bg-brand-primary text-black'
                    : isFailed
                    ? 'bg-red-500/20 text-red-400 border border-red-500/40'
                    : isActive
                    ? 'border-2 border-brand-primary text-brand-primary'
                    : 'border border-white/20 text-white/30'
                }`}
              >
                {isDone ? (
                  <FaCheck className="text-xs" />
                ) : isActive ? (
                  <FaSpinner className="text-xs animate-spin" />
                ) : (
                  <span className="text-[10px]">{idx + 1}</span>
                )}
              </span>
              <span
                className={
                  isDone
                    ? 'text-white'
                    : isActive
                    ? 'text-brand-primary'
                    : isPending
                    ? 'text-white/40'
                    : 'text-white/40'
                }
              >
                {stage.label}
              </span>
            </li>
          );
        })}
      </ul>

      <div className="h-1.5 w-full bg-white/5 rounded overflow-hidden">
        <div
          className={`h-full transition-all duration-500 ${error ? 'bg-red-500/50' : 'bg-brand-primary'}`}
          style={{ width: `${percent}%` }}
        />
      </div>

      {error && <div className="mt-3 text-sm text-red-400">{error}</div>}
    </div>
  );
}
